"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import { useAuth } from "../../../contexts/AuthContext"
import { createPortal } from "react-dom"

function BalanceDrawer({ isOpen, onClose, title, children, processing = false }) {
  const drawerContent = (
    <div className={`fixed inset-0 z-[9999] overflow-hidden transition-all duration-300 ${isOpen ? "visible" : "invisible pointer-events-none"}`}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-gray-500/30 dark:bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"}`}
        onClick={processing ? undefined : onClose}
      ></div>

      <div className="absolute inset-y-0 right-0 max-w-full flex pl-10">
        <div
          className={`w-screen max-w-md transform transition-transform duration-300 ease-in-out bg-white dark:bg-gray-800 shadow-2xl flex flex-col justify-between ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-200/50 dark:border-gray-700/50 flex items-center justify-between bg-gray-50/50 dark:bg-gray-900/50">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(drawerContent, document.body)
}

function UserRecharge() {
  const navigate = useNavigate()
  const {
    user,
    getAllUsers,
    transferBalance,
    removeBalance,
    getTransactionHistory,
    getBalanceSummary,
    canTransferBalance,
  } = useAuth()

  // State management
  const [users, setUsers] = useState([])
  const [transactions, setTransactions] = useState([])
  const [balanceSummary, setBalanceSummary] = useState({})
  const [currentBalance, setCurrentBalance] = useState(0)
  const [pagination, setPagination] = useState({})
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)

  // Modal state
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showRemoveModal, setShowRemoveModal] = useState(false)

  // Modal Form states
  const [transferFormData, setTransferFormData] = useState({ transferToUserId: "", amount: "", description: "" })
  const [removeFormData, setRemoveFormData] = useState({ removeFromUserId: "", amount: "", description: "" })

  // Filter states
  const [filterType, setFilterType] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const ITEMS_PER_PAGE = 10
  const isNormalUser = user && ["user", "reseller"].includes(user.role)

  // Load initial data
  useEffect(() => {
    if (!isNormalUser) return

    loadUsers()
    loadBalanceSummary()
    loadTransactionHistory()
  }, [isNormalUser, user?._id])

  const loadUsers = async () => {
    const result = await getAllUsers()
    if (result.success) {
      const currentUserId = user?._id || user?.id
      const filtered = result.users.filter((u) => {
        if (String(u._id || u.id) === String(currentUserId)) return false
        if (user.role === "user") {
          return u.role === "agent" && String(u.createdBy?._id || u.createdBy || "") === String(currentUserId)
        }
        if (user.role === "reseller") {
          const creatorId = String(u.createdBy?._id || u.createdBy || "")
          const parentIds = (u.parentUsers || []).map((p) => String(p?._id || p))
          return creatorId === String(currentUserId) || parentIds.includes(String(currentUserId))
        }
        return false
      })
      setUsers(filtered)
    }
  }

  const loadTransactionHistory = async (page = 1, type = "") => {
    setLoading(true)
    // Pass user's ID as the filter target to only load their own transactions
    const currentUserId = user?._id || user?.id
    const result = await getTransactionHistory(page, ITEMS_PER_PAGE, type, currentUserId)
    if (result.success) {
      setTransactions(result.data)
      setPagination(result.pagination)
    }
    setLoading(false)
  }

  const loadBalanceSummary = async () => {
    const result = await getBalanceSummary()
    if (result.success) {
      setCurrentBalance(result.currentBalance)
      setBalanceSummary(result.summary)
    }
  }

  const handleAmountKeyDown = (e) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End"
    ];
    if (e.ctrlKey || e.metaKey || allowedKeys.includes(e.key)) return;
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  }

  const handleAmountChange = (val, setter, currentData) => {
    const clean = val.replace(/,/g, "").replace(/\D/g, "")
    const formatted = clean ? Number(clean).toLocaleString("en-IN") : ""
    setter({
      ...currentData,
      amount: formatted
    })
  }

  const getAmountValue = (value) => Number.parseFloat(String(value || "").replace(/,/g, ""))

  const isValidAmount = (value) => {
    const amount = getAmountValue(value)
    return Number.isFinite(amount) && amount > 0
  }

  const showSuccessAlert = async (message) => {
    await Swal.fire({
      icon: "success",
      title: message,
      timer: 1600,
      showConfirmButton: false,
    })
  }

  const showErrorAlert = async (message) => {
    await Swal.fire({
      icon: "error",
      title: "Transaction failed",
      text: message || "Please try again.",
    })
  }

  const handleTransferBalance = async (e) => {
    e.preventDefault()
    if (processing) return
    const amount = getAmountValue(transferFormData.amount)
    if (!transferFormData.transferToUserId || !isValidAmount(transferFormData.amount)) {
      await showErrorAlert("Please choose a recipient and enter a valid amount.")
      return
    }
    if (amount > currentBalance) {
      await showErrorAlert("Transfer amount cannot be greater than your current balance.")
      return
    }

    try {
      setProcessing(true)
      const result = await transferBalance(transferFormData.transferToUserId, amount, transferFormData.description)
      if (result.success) {
        setTransferFormData({ transferToUserId: "", amount: "", description: "" })
        setShowTransferModal(false)
        await Promise.all([loadTransactionHistory(currentPage, filterType), loadBalanceSummary(), loadUsers()])
        await showSuccessAlert("Balance transferred successfully")
      } else {
        await showErrorAlert(result.error)
      }
    } catch (error) {
      await showErrorAlert(error.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleRemoveBalance = async (e) => {
    e.preventDefault()
    if (processing) return
    const amount = getAmountValue(removeFormData.amount)
    const targetUser = users.find((u) => String(u._id || u.id) === String(removeFormData.removeFromUserId))
    const targetUserBalance = targetUser?.balance || 0

    if (!removeFormData.removeFromUserId || !isValidAmount(removeFormData.amount)) {
      await showErrorAlert("Please choose a user and enter a valid amount.")
      return
    }

    if (amount > targetUserBalance) {
      await showErrorAlert("Removed amount cannot be greater than the user's current balance.")
      return
    }

    try {
      setProcessing(true)
      const result = await removeBalance(removeFormData.removeFromUserId, amount, removeFormData.description)
      if (result && (result.success || result.transaction)) {
        setRemoveFormData({ removeFromUserId: "", amount: "", description: "" })
        setShowRemoveModal(false)
        await Promise.all([loadTransactionHistory(currentPage, filterType), loadBalanceSummary(), loadUsers()])
        await showSuccessAlert("Balance removed successfully and credited back to your account")
      } else {
        await showErrorAlert(result?.message || result?.error || "Failed to remove balance")
      }
    } catch (error) {
      await showErrorAlert(error.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleFilterChange = () => {
    setCurrentPage(1)
    loadTransactionHistory(1, filterType)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    loadTransactionHistory(page, filterType)
  }

  const getTransactionBadge = (type) => {
    const colors = {
      add: "bg-emerald-100 text-emerald-800 border border-emerald-200",
      remove: "bg-rose-100 text-rose-800 border border-rose-200",
      transfer: "bg-blue-100 text-blue-800 border border-blue-200",
      refund: "bg-amber-100 text-amber-800 border border-amber-200",
    }
    return colors[type] || "bg-gray-100 text-gray-800 border border-gray-200"
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount)
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleString("en-IN")
  }

  if (!user || !isNormalUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600">
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
            </svg>
          </div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">Access Denied</h1>
          <p className="mb-6 text-gray-600 dark:text-gray-300">This balance page is restricted to user accounts.</p>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="rounded-xl bg-green-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50/50 to-yellow-50/30 dark:from-gray-900 dark:to-gray-950 p-6 md:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-200/50 dark:border-gray-800/50 pb-6">
          <div className="text-left">
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              Balance Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Transfer balance to your agents and view comprehensive transaction history.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {canTransferBalance() && (
              <button
                type="button"
                onClick={() => setShowTransferModal(true)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white px-5 py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Transfer Balance
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowRemoveModal(true)}
              className="flex items-center gap-2 bg-rose-600 hover:bg-rose-700 active:scale-95 text-white px-5 py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition-all duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
              Remove Balance
            </button>
          </div>
        </div>

        {/* Dashboard Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Current Balance Card */}
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-700 dark:from-blue-700 dark:to-indigo-800 p-6 rounded-2xl shadow-md border border-blue-500/20 text-white flex flex-col justify-between h-44 hover:shadow-lg transition-all duration-300">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-xl pointer-events-none"></div>
            <div className="relative z-10 flex justify-between items-start">
              <div className="text-left">
                <p className="text-blue-100 text-xs font-semibold uppercase tracking-wider">Current Balance</p>
                <h3 className="text-3xl font-bold mt-2 tracking-tight">{formatCurrency(currentBalance)}</h3>
              </div>
              <div className="p-3 bg-white/15 rounded-xl backdrop-blur-sm">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
            <div className="text-left">
              <span className="text-xs text-blue-200">Available for instant transfer to agents</span>
            </div>
          </div>

          {/* Total Transfers Card */}
          <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 to-pink-700 dark:from-purple-700 dark:to-pink-850 p-6 rounded-2xl shadow-md border border-purple-500/20 text-white flex flex-col justify-between h-44 hover:shadow-lg transition-all duration-300">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-xl pointer-events-none"></div>
            <div className="relative z-10 flex justify-between items-start">
              <div className="text-left">
                <p className="text-purple-100 text-xs font-semibold uppercase tracking-wider">Total Transfers</p>
                <h3 className="text-3xl font-bold mt-2 tracking-tight">{formatCurrency(balanceSummary.transfer?.totalAmount || 0)}</h3>
              </div>
              <div className="p-3 bg-white/15 rounded-xl backdrop-blur-sm">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
            </div>
            <div className="text-left">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/15 backdrop-blur-sm text-purple-100">
                {balanceSummary.transfer?.count || 0} Transactions Completed
              </span>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-2xl p-5 shadow-sm">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1 w-full text-left">
              <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Transaction Type</label>
              <div className="relative">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full pl-4 pr-10 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 appearance-none font-semibold h-[48px]"
                >
                  <option value="">All Types</option>
                  <option value="transfer">Transfer</option>
                  <option value="refund">Refund</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="w-full md:w-auto flex items-end">
              <button
                type='button'
                onClick={handleFilterChange}
                className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold py-3 px-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 whitespace-nowrap h-[48px] flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* History Table Container */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200/50 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-900/50 text-left">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Transaction History</h3>
            <p className="text-sm text-gray-500 mt-1">Showing {ITEMS_PER_PAGE} transactions per page</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200/50">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Performed By
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Old Balance
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    New Balance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/30 divide-y divide-gray-200/30">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-gray-600">Loading transactions...</span>
                      </div>
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg
                          className="w-12 h-12 text-gray-300 mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <p className="text-lg font-medium">No transactions found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction, index) => (
                    <tr
                      key={transaction._id}
                      className={`hover:bg-white/50 transition-colors duration-200 ${index % 2 === 0 ? "bg-white/20" : "bg-white/10"}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatDate(transaction.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getTransactionBadge(transaction.type)}`}
                        >
                          {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(transaction.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.type === "transfer" ? (
                          <div className="space-y-1">
                            <div className="flex items-center text-xs">
                              <span className="text-gray-500">From:</span>
                              <span className="ml-1 font-medium">{transaction.fromUser?.username || "Unknown"}</span>
                            </div>
                            <div className="flex items-center text-xs">
                              <span className="text-gray-500">To:</span>
                              <span className="ml-1 font-medium">{transaction.toUser?.username || "Unknown"}</span>
                            </div>
                          </div>
                        ) : (
                          <span className="font-medium">{transaction.toUser?.username || "Unknown"}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {transaction.performedBy?.username || "Unknown"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                        <div className="max-w-xs">
                          <p className="truncate font-medium">{transaction.description || "-"}</p>
                          {transaction.type === "refund" && (transaction.campaignName || transaction.templateName) && (
                            <p className="text-xs text-amber-700 font-semibold truncate mt-0.5">
                              Campaign: {transaction.campaignName || transaction.templateName}
                              {transaction.metadata?.refundedCount ? ` (${transaction.metadata.refundedCount} messages)` : ""}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(transaction.previousBalance || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(transaction.newBalance)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200/50 bg-gradient-to-r from-gray-50 to-gray-100 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-semibold">{(pagination.page - 1) * pagination.limit + 1}</span> to{" "}
                <span className="font-semibold">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>{" "}
                of <span className="font-semibold">{pagination.total}</span> results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type='button'
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                  let page = i + 1
                  if (pagination.pages > 5) {
                    if (pagination.page > 3) {
                      page = pagination.page - 2 + i
                    }
                    if (page > pagination.pages) {
                      page = pagination.pages - 4 + i
                    }
                  }
                  return (
                    <button
                      type='button'
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${page === pagination.page
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                      {page}
                    </button>
                  )
                })}
                <button
                  type='button'
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        <BalanceDrawer isOpen={showTransferModal} onClose={() => { setShowTransferModal(false); setTransferFormData({ transferToUserId: "", amount: "", description: "" }); }} title="Transfer Balance" processing={processing}>
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm text-blue-800 text-left">
              Your current balance: <span className="font-semibold">{formatCurrency(currentBalance)}</span>
            </p>
          </div>
          <form onSubmit={handleTransferBalance} className="space-y-4">
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">Transfer To</label>
              <select
                value={transferFormData.transferToUserId}
                onChange={(e) => setTransferFormData({ ...transferFormData, transferToUserId: e.target.value })}
                disabled={processing}
                required
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">Choose recipient...</option>
                {users.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.username} ({u.email}) - {formatCurrency(u.balance || 0)}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg">₹</span>
                <input
                  type="text"
                  id="txtAmount"
                  placeholder="Enter amount to transfer"
                  value={transferFormData.amount}
                  onKeyDown={handleAmountKeyDown}
                  onChange={(e) => handleAmountChange(e.target.value, setTransferFormData, transferFormData)}
                  autoComplete="off"
                  disabled={processing}
                  required
                  className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                />
              </div>
            </div>
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
              <textarea
                value={transferFormData.description}
                onChange={(e) => setTransferFormData({ ...transferFormData, description: e.target.value })}
                rows="3"
                disabled={processing}
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter description for this transfer"
              />
            </div>
            <button
              type="submit"
              disabled={
                processing ||
                !transferFormData.transferToUserId ||
                !isValidAmount(transferFormData.amount) ||
                getAmountValue(transferFormData.amount) > currentBalance
              }
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 px-6 rounded-xl font-semibold shadow-sm hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                "Transfer Balance"
              )}
            </button>
          </form>
        </BalanceDrawer>

        <BalanceDrawer
          isOpen={showRemoveModal}
          onClose={() => { setShowRemoveModal(false); setRemoveFormData({ removeFromUserId: "", amount: "", description: "" }); }}
          title="Remove Balance from User"
          processing={processing}
        >
          <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700/50 rounded-xl">
            <p className="text-sm text-amber-800 dark:text-amber-300 text-left">
              Removed balance will be deducted from the selected user's balance and <strong>credited directly into your account</strong>.
            </p>
          </div>
          <form onSubmit={handleRemoveBalance} className="space-y-4">
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Remove From User</label>
              <select
                value={removeFormData.removeFromUserId}
                onChange={(e) => setRemoveFormData({ ...removeFormData, removeFromUserId: e.target.value })}
                disabled={processing}
                required
                className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Choose user...</option>
                {users.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.username} ({u.email}) - {formatCurrency(u.balance || 0)}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 font-semibold text-lg">₹</span>
                <input
                  type="text"
                  placeholder="Enter amount to remove"
                  value={removeFormData.amount}
                  onKeyDown={handleAmountKeyDown}
                  onChange={(e) => handleAmountChange(e.target.value, setRemoveFormData, removeFormData)}
                  autoComplete="off"
                  disabled={processing}
                  required
                  className="w-full pl-9 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description (Optional)</label>
              <textarea
                value={removeFormData.description}
                onChange={(e) => setRemoveFormData({ ...removeFormData, description: e.target.value })}
                rows="3"
                disabled={processing}
                className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Enter description or reason for balance removal"
              />
            </div>
            <button
              type="submit"
              disabled={
                processing ||
                !removeFormData.removeFromUserId ||
                !isValidAmount(removeFormData.amount)
              }
              className="w-full bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white py-3 px-6 rounded-xl font-semibold shadow-sm hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                "Remove Balance & Credit to My Account"
              )}
            </button>
          </form>
        </BalanceDrawer>
      </div>
    </div>
  )
}

export default UserRecharge
